import styled from 'styled-components';
import { useDrfqMakerOrders } from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import { blackTangaroa } from '@paradigm/design-system/src/colors';
import { useDrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import { primaryProductCode } from '@paradigm/features/src/unified-rfqs/utils/legs';
import {
  useUMPlatformCreationDisabled,
  useUMPlatformKillDisabled,
} from '@paradigm/features/src/platform-state/contexts/providers';
import {
  KillCell,
  MyAsk,
  MyBid,
  MyOrdersContainer,
  PriceCell,
  QtyCell,
} from '../components/RfqRowBase';
import KillOrderButton from './KillOrderButton';
import { stopPropagation } from './common';
import QuoteDrfqButton from './QuoteDrfqButton';
import { MakerPriceButton } from './OrderPriceButton';

export default function MakerOrders() {
  const view = useDrfqMakerOrders();
  const drfq = useDrfqRfq();
  const productCode = primaryProductCode(drfq.legs) ?? drfq.product_codes[0];
  const isDated = isDatedRfqLegs(drfq.legs, productCode);
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  switch (view.state) {
    case 'LOADING_ORDERS':
    case 'CLOSED_AUCTION':
      return null;
    case 'OPEN_AUCTION': {
      const { bid, ask } = view;
      const isEmpty = bid == null && ask == null;

      if (isEmpty) {
        return (
          <QuoteContainer
            css="width: 345px; margin: auto;"
            onClick={stopPropagation()}
          >
            <QuoteDrfqButton side={null} />
          </QuoteContainer>
        );
      }

      return (
        <MyOrdersContainer onClick={stopPropagation()}>
          {bid != null ? (
            <MyBid>
              <QtyCell disabled={isPlatformCreationDisabled}>
                {formatProductQty(bid.quantity, productCode, true, isDated)}
              </QtyCell>
              <KillCell>
                <KillOrderButton
                  disabled={isPlatformKillDisabled}
                  orderId={bid.id}
                />
              </KillCell>
              <PriceCell disabled={isPlatformCreationDisabled}>
                <MakerPriceButton
                  disabled={isPlatformCreationDisabled}
                  side="BUY"
                  order={bid}
                />
              </PriceCell>
            </MyBid>
          ) : (
            <QuoteContainer gridArea="bid">
              <QuoteDrfqButton side="BUY" />
            </QuoteContainer>
          )}
          {ask != null ? (
            <MyAsk>
              <QtyCell disabled={isPlatformCreationDisabled}>
                {formatProductQty(ask.quantity, productCode, true, isDated)}
              </QtyCell>
              <KillCell>
                <KillOrderButton
                  disabled={isPlatformKillDisabled}
                  orderId={ask.id}
                />
              </KillCell>
              <PriceCell disabled={isPlatformCreationDisabled}>
                <MakerPriceButton
                  disabled={isPlatformCreationDisabled}
                  side="SELL"
                  order={ask}
                />
              </PriceCell>
            </MyAsk>
          ) : (
            <QuoteContainer gridArea="ask">
              <QuoteDrfqButton side="SELL" />
            </QuoteContainer>
          )}
        </MyOrdersContainer>
      );
    }
    // no default
  }
}

const QuoteContainer = styled.div<{ gridArea?: string }>`
  ${(props) => props.gridArea != null && `grid-area: ${props.gridArea};`}
  height: 30px;
  border-radius: 3px;
  display: flex;
  width: 100%;
  background: ${blackTangaroa};
  justify-content: center;
  align-items: center;
`;
